import type { PriceQuote } from '@ducat-unit/core';

import { FRONTEND_DEPLOYMENT } from '@/config/deployment';
import { SERVICE_URLS } from '@/config/service-config';
import { getValidatorWsUrl } from '@/config/validator-ws';

export { ERRORS } from './errors';

export const LARGE_ICON_SIZE = 6;
export const SMALL_ICON_SIZE = 4;
export const BTC_BALANCE = 1.1293;
export const UNIT_BALANCE = 1000;
export const UNIT_PRICE = 1;
export const MIN_COL_RATE = 1.6;
export const LIQUIDATION_RATE = 1.35;
export const LIQ_MAX_CLAIM_AMOUNT_BTC = 0.5;
export const SWAP_MAX_AMOUNT_UNIT = Number(process.env.NEXT_PUBLIC_SWAP_MAX_AMOUNT_UNIT) || Infinity;
export const MAX_HEALTH_DISPLAY = 1000;

/** Vault health factor zone borders (percent). */
export const HEALTH_DISPLAY = {
  RISKY_MIN: 135,
  RISKY_MAX: 160,
  MODERATE_MIN: 161,
  MODERATE_MAX: 200,
  HEALTHY_MIN: 201,
  HEALTHY_MAX: 300,
};

export const FeeEstimates = [
  {
    price: '$ 0.75',
    speed: 'Slow',
    time: '~ 2 Min',
    statusColor: 'bg-red-100',
  },
  {
    price: '$ 2.25',
    speed: 'Medium',
    time: '~ 1 Min',
    statusColor: 'bg-yellow-100',
  },
  {
    price: '$ 5.15',
    speed: 'Fast',
    time: '~ 30 Sec',
    statusColor: 'bg-green-100',
  },
];

// swap rate: 1 unit = 1.02 btc
export const UNIT_TO_BTC_RATE = 1.02;

export const IS_DEV = process.env.NEXT_PUBLIC_NODE_ENV === 'development';
export const DEV_API_MOCK = process.env.NEXT_PUBLIC_API_MOCK === 'true';
// Dev/regtest-only: let the liquidations claim flow self-publish the breach
// attestation to the oracle relay (POST /api/breach) when none is found yet, so
// a keeper can claim a freshly-distressed vault without an out-of-band publisher.
// Off in prod (breaches are published by the oracle/keeper ecosystem).
export const DEV_LIQ_TRIGGER = process.env.NEXT_PUBLIC_DEV_LIQ_TRIGGER === 'true';

// NEXT_PUBLIC_NETWORK is parsed once; legacy default/enabled variables remain
// profile compatibility inputs and cannot become runtime authority.
export const DEFAULT_NETWORK = FRONTEND_DEPLOYMENT.network;

export const DEFAULT_PRICE_QUOTE: PriceQuote = {
  base_price: 0,
  base_stamp: 0,
  chain_network: DEFAULT_NETWORK,
  oracle_pubkey: '',
  rate_min: MIN_COL_RATE,
  rate_max: 5,
  rate_thold: LIQUIDATION_RATE,
  step_size: 0.01,
};

export const ESPLORA_URL = process.env.NEXT_PUBLIC_ESPLORA_URL || 'https://mutinynet.com/api';

// https://t.me/ducat_transaction_bot?start=_<user-address>_<network>
export const TELEGRAM_OPEN_URL = process.env.NEXT_PUBLIC_TELEGRAM_OPEN_URL || 'https://t.me/ducat_transaction_bot';
export const TELEGRAM_NOTIFY_URL = process.env.NEXT_PUBLIC_TELEGRAM_NOTIFY_URL || 'https://notifications.ducatprotocol.com';

export const XVERSE_MIGRATE_URL =
  process.env.NEXT_PUBLIC_XVERSE_MIGRATE_URL ||
  'https://support.xverse.app/hc/en-us/articles/31414340748301-How-to-Switch-My-Preferred-Bitcoin-Address-in-Xverse';

export const RANKING_API_URL = process.env.NEXT_PUBLIC_RANKING_API_URL || 'https://ranking.ducatprotocol.com';
export const LIQ_BUCKET_GET_URL = `${RANKING_API_URL}/roundrobin/get`;
export const RESERVE_SUMMARY_URL = `${RANKING_API_URL}/reserve/summary`;
export const RESERVE_CHART_URL = `${RANKING_API_URL}/reserve/chart`;
export const RESERVE_TXS_URL = `${RANKING_API_URL}/reserve/transactions`;

export const FAUCET_API_URL = process.env.NEXT_PUBLIC_FAUCET_API_URL || 'https://faucet.ducatprotocol.com';
export const FAUCET_BTC_URL = `${FAUCET_API_URL}/btc/faucet`;
export const FAUCET_UNIT_URL = `${FAUCET_API_URL}/unit/faucet`;
// export const FAUCET_UNIT_SWAP_URL = `${FAUCET_API_URL}/unit/swap`;
// export const FAUCET_UNIT_SWAP_URL = `${FAUCET_API_URL}/unit/faucet/test`;
export const FAUCET_UNIT_SWAP_URL = `${FAUCET_API_URL}/unit/faucet/test`;

export const VALIDATOR_SERVER = process.env.NEXT_PUBLIC_VALIDATOR_SERVER || SERVICE_URLS.validator;
export const VALIDATOR_URL = process.env.NEXT_PUBLIC_VALIDATOR_URL || `https://${VALIDATOR_SERVER}`;
export const VALIDATOR_WS = getValidatorWsUrl({
  validatorServer: VALIDATOR_SERVER,
  explicitWsUrl: process.env.NEXT_PUBLIC_VALIDATOR_WS,
});

export const LIQ_VALIDATOR_SERVER = process.env.NEXT_PUBLIC_LIQ_VALIDATOR_SERVER || SERVICE_URLS.liq_validator;
export const LIQ_VALIDATOR_URL = `https://${LIQ_VALIDATOR_SERVER}/liq`;
export const LIQ_VALIDATOR_WS = `wss://${LIQ_VALIDATOR_SERVER}/liq/ws`;

// Timeout to capture user being too slow to sign:
export const TRANSACTION_ACTION_TIMEOUT_MS = 90_000;
// Timeout for guardian connection:
export const TX_TIMEOUT = process.env.NEXT_PUBLIC_TX_TIMEOUT ? Number(process.env.NEXT_PUBLIC_TX_TIMEOUT) : 60_000;

export const LIQUIDATED_VAULTS_TO_INCLUDE = process.env.NEXT_PUBLIC_LIQUIDATED_VAULTS_TO_INCLUDE || '';
export const LIQUIDATED_VAULTS_TO_EXCLUDE = process.env.NEXT_PUBLIC_LIQUIDATED_VAULTS_TO_EXCLUDE || '';
export const LIQUIDATED_VAULTS_MATCH_LEN = 6;

export const LIQ_NUMBER_OF_BUCKETS = process.env.NEXT_PUBLIC_LIQ_NUMBER_OF_BUCKETS
  ? Number(process.env.NEXT_PUBLIC_LIQ_NUMBER_OF_BUCKETS)
  : 1;

export const ALLOW_GUARDIAN_RANDOMIZE = process.env.NEXT_PUBLIC_DISABLE_GUARDIAN_RANDOMIZE !== 'true';
export const GUARDIAN_NUM_FORCED = process.env.NEXT_PUBLIC_GUARDIAN_NUM_FORCED
  ? Number(process.env.NEXT_PUBLIC_GUARDIAN_NUM_FORCED)
  : undefined;
export const DISABLED_GUARDIAN_NUMS = process.env.NEXT_PUBLIC_DISABLED_GUARDIAN_NUMS?.split(',') || [];

const getIsFeatureOn = (featureName: string, defaultValue: string | undefined): boolean => {
  // Dev feature toggle switch:
  const featureToggles = (globalThis.window as unknown as Record<string, unknown>)?.featureToggles || Object.create(null);

  if (typeof featureToggles[featureName] === 'boolean') {
    return featureToggles[featureName];
  }

  const localStorageValue = globalThis.window?.localStorage.getItem(featureName);

  return (localStorageValue ?? defaultValue) === 'true' || false;
};

export const FEATURES = {
  // Liquidations are retired. Stored development overrides must not re-enable them.
  liquidations: false,
  liquidationsOneClick: getIsFeatureOn(
    'NEXT_PUBLIC_FEATURE_LIQUIDATIONS_ONE_CLICK_ON',
    process.env.NEXT_PUBLIC_FEATURE_LIQUIDATIONS_ONE_CLICK_ON,
  ),
  automaticSwap: getIsFeatureOn('NEXT_PUBLIC_FEATURE_AUTOMATIC_SWAP_ON', process.env.NEXT_PUBLIC_FEATURE_AUTOMATIC_SWAP_ON),
  swap: getIsFeatureOn('NEXT_PUBLIC_FEATURE_SWAP_ON', process.env.NEXT_PUBLIC_FEATURE_SWAP_ON),
  swapDrawer: getIsFeatureOn('NEXT_PUBLIC_FEATURE_SWAP_DRAWER_ON', process.env.NEXT_PUBLIC_FEATURE_SWAP_DRAWER_ON),
  bucketRoundrobin: getIsFeatureOn('NEXT_PUBLIC_FEATURE_BUCKET_ROUNDROBIN_ON', process.env.NEXT_PUBLIC_FEATURE_BUCKET_ROUNDROBIN_ON),
  liquidationDebug: getIsFeatureOn('NEXT_PUBLIC_FEATURE_LIQUIDATIONS_DEBUG', process.env.NEXT_PUBLIC_FEATURE_LIQUIDATIONS_DEBUG),
  chainlinkBanner: getIsFeatureOn('NEXT_PUBLIC_FEATURE_CHAINLINK_BANNER_ON', process.env.NEXT_PUBLIC_FEATURE_CHAINLINK_BANNER_ON),
  notificationCenter: getIsFeatureOn('NEXT_PUBLIC_FEATURE_NOTIFICATION_CENTER_ON', process.env.NEXT_PUBLIC_FEATURE_NOTIFICATION_CENTER_ON),
  reserve: getIsFeatureOn('NEXT_PUBLIC_FEATURE_RESERVE_ON', process.env.NEXT_PUBLIC_FEATURE_RESERVE_ON),
  // Mission 4 "Go Mainnet" cross-network wallet linking — alpha-release only.
  mainnetLink: getIsFeatureOn('NEXT_PUBLIC_FEATURE_MAINNET_LINK_ON', process.env.NEXT_PUBLIC_FEATURE_MAINNET_LINK_ON),
  settingsCloseAction: getIsFeatureOn(
    'NEXT_PUBLIC_FEATURE_SETTINGS_CLOSE_ACTION_ON',
    process.env.NEXT_PUBLIC_FEATURE_SETTINGS_CLOSE_ACTION_ON,
  ),
  i18n: getIsFeatureOn('NEXT_PUBLIC_FEATURE_I18N_ON', process.env.NEXT_PUBLIC_FEATURE_I18N_ON),
  metamaskOn: getIsFeatureOn('NEXT_PUBLIC_FEATURE_METAMASK_ON', process.env.NEXT_PUBLIC_FEATURE_METAMASK_ON),
  unitBridgePageOn: getIsFeatureOn('NEXT_PUBLIC_FEATURE_UNIT_BRIDGE_PAGE_ON', process.env.NEXT_PUBLIC_FEATURE_UNIT_BRIDGE_PAGE_ON),
  liquidityPoolPageOn: getIsFeatureOn('NEXT_PUBLIC_FEATURE_LIQUIDITY_POOL_PAGE_ON', process.env.NEXT_PUBLIC_FEATURE_LIQUIDITY_POOL_PAGE_ON),
  liquidityPoolUsdcUsdtOn: getIsFeatureOn(
    'NEXT_PUBLIC_FEATURE_LIQUIDITY_POOL_USDC_USDT_ON',
    process.env.NEXT_PUBLIC_FEATURE_LIQUIDITY_POOL_USDC_USDT_ON,
  ),
};

// export const ORACLE_PUBKEY = process.env.NEXT_PUBLIC_ORACLE_PUBKEY ?? '3752ade673753a7be34f4b24486151e66e7df781bde6d0c4cababd94aaa45a7a';
// export const ORACLE_URL = process.env.NEXT_PUBLIC_ORACLE_URL ?? 'ws://localhost:8082';
export const ORACLE_HTTP = process.env.NEXT_PUBLIC_ORACLE_HTTP ?? 'http://localhost:8082';

// export const ORACLE_RELAY_URL = process.env.NEXT_PUBLIC_ORACLE_RELAY_URL ?? 'wss://dev-relay.ducatprotocol.com';

// todo: default to mutiny
export const GUARDIAN_URL_HTTP = process.env.NEXT_PUBLIC_GUARDIAN_URL_HTTP ?? `http://localhost:8081`;
// export const GUARDIAN_URL = process.env.NEXT_PUBLIC_GUARDIAN_URL ?? `ws://localhost:8081`;
// export const GUARDIAN_PK = process.env.NEXT_PUBLIC_GUARDIAN_PK ?? `f25e397cbe8e5d38d2a2dd9aefbd579956bd8f19c4ea9a5db4a2d3d669f5f2f8`;

export const VALIDATOR_WS_EVENT = 'new';
